import React from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import { useTranslation } from "react-i18next";
import { CodInvoice, CodOrderInvoices } from "../../../types/codSalesRep";
import {
  codInvoiceStatusKey,
  codInvoiceStatusTone,
  codNotPayableKey,
  formatCodAmount,
  formatCodDate,
} from "../../../utils/codUtils";
import StatusChip from "../../../components/ui/StatusChip";
import { NJIM } from "./codStyles";

interface CodInvoiceTreeProps {
  orders: CodOrderInvoices[];
  selectedInvoiceIds: string[];
  onToggleInvoice: (invoiceId: string) => void;
  /** Hide checkboxes (read-only view). */
  readOnly?: boolean;
}

/**
 * COD order → lines (sub-orders) → Sage invoices.
 * Only invoices carry a checkbox, and only when the API marks them isPayable.
 */
const CodInvoiceTree: React.FC<CodInvoiceTreeProps> = ({
  orders,
  selectedInvoiceIds,
  onToggleInvoice,
  readOnly = false,
}) => {
  const { t } = useTranslation();

  const renderInvoice = (invoice: CodInvoice, currency: string) => {
    const selected = selectedInvoiceIds.includes(invoice.invoiceId);
    const reasonKey = codNotPayableKey(invoice.notPayableReason);
    return (
      <Box
        key={invoice.invoiceId}
        onClick={() => !readOnly && invoice.isPayable && onToggleInvoice(invoice.invoiceId)}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "auto minmax(0, 1fr) auto auto", md: "auto minmax(0, 1.4fr) auto repeat(3, minmax(0, 0.8fr))" },
          alignItems: "center",
          gap: { xs: 1, md: 2 },
          px: 1.25,
          py: 0.75,
          borderRadius: "10px",
          border: `1px solid ${selected ? NJIM.mint : NJIM.border}`,
          bgcolor: selected ? NJIM.mintWash : NJIM.white,
          cursor: !readOnly && invoice.isPayable ? "pointer" : "default",
          transition: `border-color 150ms ${NJIM.easeOut}, background-color 150ms ${NJIM.easeOut}`,
        }}
      >
        <Box sx={{ width: 42, display: "flex", justifyContent: "center" }}>
          {!readOnly && invoice.isPayable ? (
            <Checkbox
              size="small"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleInvoice(invoice.invoiceId)}
              inputProps={{ "aria-label": t("codSalesRep.invoiceNo", { number: invoice.invoiceNumber }) }}
            />
          ) : null}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.ink }}>
            {t("codSalesRep.invoiceNo", { number: invoice.invoiceNumber })}
          </Typography>
          <Typography sx={{ fontSize: 11, color: NJIM.textSecondary }}>
            {invoice.invoiceDate ? formatCodDate(invoice.invoiceDate) : "—"}
            {!invoice.isPayable && reasonKey ? ` · ${t(reasonKey)}` : ""}
          </Typography>
        </Box>
        <StatusChip
          status={codInvoiceStatusTone(invoice.paymentStatus)}
          label={t(codInvoiceStatusKey(invoice.paymentStatus))}
        />
        <Money label={t("codSalesRep.invoiceAmount")} value={formatCodAmount(invoice.amount, currency)} />
        <Money label={t("codSalesRep.invoicePaid")} value={formatCodAmount(invoice.paidAmount, currency)} color={NJIM.success} />
        <Money
          label={t("codSalesRep.invoiceRemaining")}
          value={formatCodAmount(invoice.remainingAmount, currency)}
          color={invoice.remainingAmount > 0.005 ? NJIM.danger : NJIM.textMuted}
          alwaysShow
        />
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {orders.map((order) => (
        <Box
          key={order.orderId}
          sx={{
            borderRadius: "14px",
            border: `1px solid ${NJIM.border}`,
            bgcolor: NJIM.white,
            overflow: "hidden",
          }}
        >
          {/* Order header — displays exactly as the order screens do */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              px: 2,
              py: 1.25,
              bgcolor: NJIM.muted,
              borderBottom: `1px solid ${NJIM.border}`,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: NJIM.ink }}>{order.orderNumber}</Typography>
              <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                {order.customerCode}
                {order.customerName && order.customerName !== order.customerCode ? ` · ${order.customerName}` : ""}
                {order.orderDate ? ` · ${formatCodDate(order.orderDate)}` : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2.5 }}>
              <Money label={t("common.total")} value={formatCodAmount(order.orderTotal, order.currency)} alwaysShow />
              <Money
                label={t("codSalesRep.invoiceRemaining")}
                value={formatCodAmount(order.totalRemaining, order.currency)}
                color={order.totalRemaining > 0.005 ? NJIM.danger : NJIM.textMuted}
                alwaysShow
              />
            </Box>
          </Box>

          {/* Lines (sub-orders), in the order the API gives them */}
          <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
            {order.lines.map((line) => (
              <Box key={line.reference}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                  <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 16, color: NJIM.textMuted, "[dir='rtl'] &": { transform: "scaleX(-1)" } }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary }}>{line.reference}</Typography>
                  {line.category && (
                    <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>· {line.category}</Typography>
                  )}
                  <Typography sx={{ fontSize: 12, color: NJIM.textMuted, marginInlineStart: "auto", fontVariantNumeric: "tabular-nums" }}>
                    {formatCodAmount(line.lineTotal, order.currency)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, paddingInlineStart: { xs: 1, md: 3 } }}>
                  {line.hasInvoice && line.invoices.length > 0 ? (
                    line.invoices.map((invoice) => renderInvoice(invoice, order.currency))
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: "10px",
                        border: `1px dashed ${NJIM.border}`,
                        color: NJIM.textSecondary,
                      }}
                    >
                      <HourglassEmptyRoundedIcon sx={{ fontSize: 16, color: NJIM.warning }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{t("codSalesRep.awaitingSageInvoice")}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const Money: React.FC<{ label: string; value: string; color?: string; alwaysShow?: boolean }> = ({
  label,
  value,
  color = NJIM.textPrimary,
  alwaysShow = false,
}) => (
  <Box sx={{ textAlign: "end", minWidth: 0, display: alwaysShow ? "block" : { xs: "none", md: "block" } }}>
    <Typography sx={{ fontSize: 10.5, color: NJIM.textSecondary, fontWeight: 600, whiteSpace: "nowrap" }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 800, color, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {value}
    </Typography>
  </Box>
);

export default CodInvoiceTree;
